import { useEffect, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { Sport, SportSessionTypes, TrainingType } from "../types/activity";
import { collection, doc, getDoc, getDocs, setDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase/config";
import { ArrowPathIcon, ArrowsPointingInIcon, LockClosedIcon, PencilIcon, PlusIcon } from "@heroicons/react/24/outline";
import { getSpecialTrainingType, isSpecialTrainingType } from "../constants/specialTrainingTypes";

type TabKey = "sports" | "trainingTypes";

// De-duplicate a list of strings, trimming and preserving first-seen order (case-sensitive).
const dedupeStrings = (values: (string | undefined)[]): string[] => {
    const result: string[] = [];
    const seen = new Set<string>();
    values.forEach((value) => {
        const trimmed = value?.trim();
        if (!trimmed || seen.has(trimmed)) return;
        seen.add(trimmed);
        result.push(trimmed);
    });
    return result;
};

const TrainingTypesAndSportsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabKey>("sports");

    // Sports state
    const [sports, setSports] = useState<Sport[]>([]);
    const [sportsLoading, setSportsLoading] = useState(true);
    const [sportsError, setSportsError] = useState<string | null>(null);
    const [generatingSports, setGeneratingSports] = useState(false);

    // Sport edit state
    const [editingSport, setEditingSport] = useState<Sport | null>(null);
    const [editSportName, setEditSportName] = useState("");
    const [editSportTrainingTypeIds, setEditSportTrainingTypeIds] = useState<Set<string>>(new Set());
    const [editSportFilter, setEditSportFilter] = useState("");
    const [savingSport, setSavingSport] = useState(false);

    // Training types state
    const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
    const [trainingTypesLoading, setTrainingTypesLoading] = useState(true);
    const [trainingTypesError, setTrainingTypesError] = useState<string | null>(null);
    const [generatingTrainingTypes, setGeneratingTrainingTypes] = useState(false);
    const [trainingTypeFilter, setTrainingTypeFilter] = useState("");
    const [expandedSportLists, setExpandedSportLists] = useState<Set<string>>(new Set());

    // Combine state
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [combineOpen, setCombineOpen] = useState(false);
    const [combineName, setCombineName] = useState("");
    const [combineModifier, setCombineModifier] = useState(1);
    const [combining, setCombining] = useState(false);

    // Rename state
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameName, setRenameName] = useState("");
    const [renaming, setRenaming] = useState(false);

    // Training type edit state
    const [editingTrainingType, setEditingTrainingType] = useState<TrainingType | null>(null);
    const [editTtName, setEditTtName] = useState("");
    const [editTtModifier, setEditTtModifier] = useState(1);
    const [editTtGps, setEditTtGps] = useState(false);
    const [editTtSportRefs, setEditTtSportRefs] = useState<Set<string>>(new Set());
    const [editTtSportFilter, setEditTtSportFilter] = useState("");
    const [savingTrainingType, setSavingTrainingType] = useState(false);

    // Add training type state
    const [addTtOpen, setAddTtOpen] = useState(false);
    const [addTtName, setAddTtName] = useState("");
    const [addTtModifier, setAddTtModifier] = useState(1);
    const [addTtGps, setAddTtGps] = useState(false);
    const [addTtSportRefs, setAddTtSportRefs] = useState<Set<string>>(new Set());
    const [addTtSportFilter, setAddTtSportFilter] = useState("");
    const [addingTrainingType, setAddingTrainingType] = useState(false);
    const [addTtError, setAddTtError] = useState<string | null>(null);

    const fetchSports = async () => {
        try {
            setSportsLoading(true);
            setSportsError(null);
            const snap = await getDocs(collection(db, "sports"));
            const data = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Sport, "id">) }));
            data.sort((a, b) => a.name.localeCompare(b.name));
            setSports(data);
        } catch (error: any) {
            console.error("Error fetching sports:", error);
            setSportsError(`Failed to fetch sports: ${error.message}`);
        } finally {
            setSportsLoading(false);
        }
    };

    const fetchTrainingTypes = async () => {
        try {
            setTrainingTypesLoading(true);
            setTrainingTypesError(null);
            const snap = await getDocs(collection(db, "trainingTypes"));
            const data = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TrainingType, "id">) }));
            data.sort((a, b) => a.name.localeCompare(b.name));
            setTrainingTypes(data);
        } catch (error: any) {
            console.error("Error fetching training types:", error);
            setTrainingTypesError(`Failed to fetch training types: ${error.message}`);
        } finally {
            setTrainingTypesLoading(false);
        }
    };

    useEffect(() => {
        fetchSports();
        fetchTrainingTypes();
    }, []);

    // Read the legacy sessionType/sports document that holds the old system.
    const fetchLegacySportsData = async (): Promise<Record<string, SportSessionTypes>> => {
        const legacySnap = await getDoc(doc(db, "sessionType", "sports"));
        if (!legacySnap.exists()) return {};
        return legacySnap.data() as Record<string, SportSessionTypes>;
    };

    // Generate the new sports collection from the old sessionTypes system.
    const handleGenerateSports = async () => {
        if (generatingSports) return;
        const confirmed = window.confirm(
            "Generate the sports collection from the old sessionTypes system? Existing sports with matching IDs will be overwritten."
        );
        if (!confirmed) return;

        try {
            setGeneratingSports(true);
            setSportsError(null);

            const legacy = await fetchLegacySportsData();
            const legacySports = Object.values(legacy);

            if (legacySports.length === 0) {
                window.alert("No sports found in the old sessionTypes system.");
                return;
            }

            const batch = writeBatch(db);
            legacySports.forEach((legacySport) => {
                if (!legacySport.sportName?.trim()) return;
                const sport: Sport = {
                    name: legacySport.sportName.trim(),
                    trainingTypeIDs: [],
                };
                // Use Firebase's auto-generated document ID.
                batch.set(doc(collection(db, "sports")), sport);
            });

            await batch.commit();
            await fetchSports();
            window.alert(`Generated ${legacySports.length} sports.`);
        } catch (error: any) {
            console.error("Error generating sports:", error);
            setSportsError(`Failed to generate sports: ${error.message}`);
        } finally {
            setGeneratingSports(false);
        }
    };

    const handleOpenEditSport = (sport: Sport) => {
        setEditingSport(sport);
        setEditSportName(sport.name);
        setEditSportTrainingTypeIds(new Set(sport.trainingTypeIDs ?? []));
        setEditSportFilter("");
    };

    const toggleEditSportTrainingType = (id: string) => {
        setEditSportTrainingTypeIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSaveSport = async () => {
        if (savingSport) return;
        if (!editingSport?.id) return;
        if (!editSportName.trim()) return;

        try {
            setSavingSport(true);
            setSportsError(null);

            // Only persist IDs that still exist in the trainingTypes collection.
            const validIds = trainingTypes.map((t) => t.id).filter((id): id is string => !!id);
            const trainingTypeIDs = validIds.filter((id) => editSportTrainingTypeIds.has(id));

            await setDoc(doc(db, "sports", editingSport.id), {
                name: editSportName.trim(),
                trainingTypeIDs,
            } satisfies Sport);

            setEditingSport(null);
            await fetchSports();
        } catch (error: any) {
            console.error("Error saving sport:", error);
            setSportsError(`Failed to save sport: ${error.message}`);
        } finally {
            setSavingSport(false);
        }
    };

    // Generate the new trainingTypes collection from the old sessionTypes system.
    // Training types are flattened across all sports and de-duplicated by name,
    // since they are no longer related to a specific sport. The sports that had a given
    // session type are recorded in sportReferenceList.
    const handleGenerateTrainingTypes = async () => {
        if (generatingTrainingTypes) return;
        const confirmed = window.confirm(
            "Generate the trainingTypes collection from the old sessionTypes system?"
        );
        if (!confirmed) return;

        try {
            setGeneratingTrainingTypes(true);
            setTrainingTypesError(null);

            const legacy = await fetchLegacySportsData();
            const legacySports = Object.values(legacy);

            // Group by exact trimmed session-type name so the same name across multiple
            // sports collapses into one training type with a combined sportReferenceList.
            const byName = new Map<string, TrainingType>();
            legacySports.forEach((legacySport) => {
                const sportName = legacySport.sportName?.trim();
                legacySport.sessionTypes?.forEach((sessionType) => {
                    const name = sessionType.name?.trim();
                    if (!name) return;
                    const existing = byName.get(name);
                    if (existing) {
                        if (sportName && !existing.sportReferenceList.includes(sportName)) {
                            existing.sportReferenceList.push(sportName);
                        }
                        return;
                    }
                    byName.set(name, {
                        name,
                        modifier: sessionType.modifier,
                        description: sessionType.example || "",
                        includeGPS: false,
                        alternativeNames: [name],
                        sportReferenceList: sportName ? [sportName] : [],
                    });
                });
            });

            if (byName.size === 0) {
                window.alert("No training types found in the old sessionTypes system.");
                return;
            }

            const batch = writeBatch(db);
            byName.forEach((trainingType) => {
                // Use Firebase's auto-generated document ID.
                batch.set(doc(collection(db, "trainingTypes")), trainingType);
            });

            await batch.commit();
            await fetchTrainingTypes();
            window.alert(`Generated ${byName.size} training types.`);
        } catch (error: any) {
            console.error("Error generating training types:", error);
            setTrainingTypesError(`Failed to generate training types: ${error.message}`);
        } finally {
            setGeneratingTrainingTypes(false);
        }
    };

    const toggleSelected = (id: string) => {
        // Special training types are protected: never let them enter a selection that
        // feeds the rename/combine actions.
        if (isSpecialTrainingType(id)) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleExpandedSportList = (id: string) => {
        setExpandedSportLists((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectedTrainingTypes = trainingTypes.filter((t) => t.id && selectedIds.has(t.id));

    const trainingTypeNameById = new Map(trainingTypes.filter((t) => t.id).map((t) => [t.id as string, t.name]));

    // Reverse lookup: training type ID -> names of sports that link to it.
    const sportNamesByTrainingTypeId = new Map<string, string[]>();
    sports.forEach((sport) => {
        (sport.trainingTypeIDs ?? []).forEach((id) => {
            const list = sportNamesByTrainingTypeId.get(id) ?? [];
            list.push(sport.name);
            sportNamesByTrainingTypeId.set(id, list);
        });
    });

    // Case-insensitive substring filter against the name and alternative names.
    const filterQuery = trainingTypeFilter.trim().toLowerCase();
    const filteredTrainingTypes = filterQuery
        ? trainingTypes.filter((t) => {
              const haystack = [t.name, t.id, ...(t.alternativeNames ?? [])].join(" ").toLowerCase();
              return haystack.includes(filterQuery);
          })
        : trainingTypes;

    // Merge the combined name plus every selected type's name AND its existing
    // alternativeNames into a single de-duplicated list. De-dup is exact (trimmed only,
    // case-sensitive) so variants like "Warm up", "Warm-up" and "Warm Up" are all kept.
    const buildMergedAlternativeNames = (name: string, types: TrainingType[]): string[] =>
        dedupeStrings([name, ...types.flatMap((t) => [t.name, ...(t.alternativeNames ?? [])])]);

    // Merge the sportReferenceList across every selected type.
    const buildMergedSportReferenceList = (types: TrainingType[]): string[] =>
        dedupeStrings(types.flatMap((t) => t.sportReferenceList ?? []));

    const mergedAlternativeNames = buildMergedAlternativeNames(combineName, selectedTrainingTypes);
    const mergedSportReferenceList = buildMergedSportReferenceList(selectedTrainingTypes);

    const handleOpenCombine = () => {
        if (selectedTrainingTypes.length < 2) return;
        const primary = selectedTrainingTypes[0];
        setCombineName(primary.name);
        setCombineModifier(primary.modifier);
        setCombineOpen(true);
    };

    // Combine several training types into one. The first selected type is kept as the
    // surviving document; the rest are deleted. All original names (and existing
    // alternative names) are preserved under alternativeNames so mappings still resolve.
    const handleConfirmCombine = async () => {
        if (combining) return;
        if (selectedTrainingTypes.length < 2) return;
        if (!combineName.trim()) return;
        // Protected types can never be merged away or absorb others.
        if (selectedTrainingTypes.some((t) => isSpecialTrainingType(t.id))) return;

        try {
            setCombining(true);
            setTrainingTypesError(null);

            const primary = selectedTrainingTypes[0];
            if (!primary.id) return;

            const merged: TrainingType = {
                name: combineName.trim(),
                modifier: combineModifier,
                description: primary.description,
                includeGPS: primary.includeGPS,
                alternativeNames: buildMergedAlternativeNames(combineName, selectedTrainingTypes),
                sportReferenceList: buildMergedSportReferenceList(selectedTrainingTypes),
            };

            const batch = writeBatch(db);
            batch.set(doc(db, "trainingTypes", primary.id), merged);
            selectedTrainingTypes.slice(1).forEach((t) => {
                if (t.id) batch.delete(doc(db, "trainingTypes", t.id));
            });
            await batch.commit();

            setCombineOpen(false);
            setSelectedIds(new Set());
            await fetchTrainingTypes();
        } catch (error: any) {
            console.error("Error combining training types:", error);
            setTrainingTypesError(`Failed to combine training types: ${error.message}`);
        } finally {
            setCombining(false);
        }
    };

    const handleOpenRename = () => {
        if (selectedTrainingTypes.length !== 1) return;
        setRenameName(selectedTrainingTypes[0].name);
        setRenameOpen(true);
    };

    // Rename a single training type. The previous name is preserved in alternativeNames
    // so any legacy mappings against the old name still resolve.
    const handleConfirmRename = async () => {
        if (renaming) return;
        if (selectedTrainingTypes.length !== 1) return;
        if (!renameName.trim()) return;

        try {
            setRenaming(true);
            setTrainingTypesError(null);

            const target = selectedTrainingTypes[0];
            if (!target.id) return;
            if (isSpecialTrainingType(target.id)) return;

            const newName = renameName.trim();
            const alternativeNames = dedupeStrings([newName, target.name, ...(target.alternativeNames ?? [])]);

            const updated: TrainingType = {
                name: newName,
                modifier: target.modifier,
                description: target.description,
                includeGPS: target.includeGPS,
                alternativeNames,
                sportReferenceList: target.sportReferenceList ?? [],
            };

            await setDoc(doc(db, "trainingTypes", target.id), updated);

            setRenameOpen(false);
            setSelectedIds(new Set());
            await fetchTrainingTypes();
        } catch (error: any) {
            console.error("Error renaming training type:", error);
            setTrainingTypesError(`Failed to rename training type: ${error.message}`);
        } finally {
            setRenaming(false);
        }
    };

    const handleOpenEditTrainingType = (trainingType: TrainingType) => {
        if (isSpecialTrainingType(trainingType.id)) return;
        setEditingTrainingType(trainingType);
        setEditTtName(trainingType.name);
        setEditTtModifier(trainingType.modifier);
        setEditTtGps(trainingType.includeGPS);
        setEditTtSportRefs(new Set(trainingType.sportReferenceList ?? []));
        setEditTtSportFilter("");
    };

    const toggleEditTtSportRef = (sportName: string) => {
        setEditTtSportRefs((prev) => {
            const next = new Set(prev);
            if (next.has(sportName)) next.delete(sportName);
            else next.add(sportName);
            return next;
        });
    };

    // Select every current sport's name as a reference for the training type being edited.
    const handleAssignEditTtToAllSports = () => {
        setEditTtSportRefs(new Set(sports.map((s) => s.name)));
    };

    // Keep each sport's trainingTypeIDs array in sync with the sport references
    // selected for a given training type. Adds the ID to newly-selected sports and
    // removes it from sports that were deselected, so the "Linked Sports" column and
    // the Sports tab reflect Assign-to-All / manual selections immediately.
    const syncSportsTrainingTypeLinks = async (trainingTypeId: string, selectedSportNames: Set<string>) => {
        const batch = writeBatch(db);
        let hasChanges = false;

        sports.forEach((sport) => {
            if (!sport.id) return;
            const currentIds = sport.trainingTypeIDs ?? [];
            const isLinked = currentIds.includes(trainingTypeId);
            const shouldBeLinked = selectedSportNames.has(sport.name);

            if (shouldBeLinked && !isLinked) {
                batch.set(doc(db, "sports", sport.id), {
                    name: sport.name,
                    trainingTypeIDs: [...currentIds, trainingTypeId],
                } satisfies Sport);
                hasChanges = true;
            } else if (!shouldBeLinked && isLinked) {
                batch.set(doc(db, "sports", sport.id), {
                    name: sport.name,
                    trainingTypeIDs: currentIds.filter((id) => id !== trainingTypeId),
                } satisfies Sport);
                hasChanges = true;
            }
        });

        if (hasChanges) await batch.commit();
    };

    const handleOpenAddTrainingType = () => {
        setAddTtName("");
        setAddTtModifier(1);
        setAddTtGps(false);
        setAddTtSportRefs(new Set());
        setAddTtSportFilter("");
        setAddTtError(null);
        setAddTtOpen(true);
    };

    const toggleAddTtSportRef = (sportName: string) => {
        setAddTtSportRefs((prev) => {
            const next = new Set(prev);
            if (next.has(sportName)) next.delete(sportName);
            else next.add(sportName);
            return next;
        });
    };

    // Select every current sport's name as a reference for the training type being created.
    const handleAssignAddTtToAllSports = () => {
        setAddTtSportRefs(new Set(sports.map((s) => s.name)));
    };

    const handleConfirmAddTrainingType = async () => {
        if (addingTrainingType) return;
        const name = addTtName.trim();
        if (!name) return;

        // Case-insensitive duplicate check against existing names and alternativeNames.
        const lowerName = name.toLowerCase();
        const isDuplicate = trainingTypes.some((t) => {
            const haystack = [t.name, ...(t.alternativeNames ?? [])].map((n) => n.toLowerCase());
            return haystack.includes(lowerName);
        });
        if (isDuplicate) {
            setAddTtError(`A training type named "${name}" already exists (or is an alternative name of one).`);
            return;
        }

        try {
            setAddingTrainingType(true);
            setAddTtError(null);

            const newTrainingType: TrainingType = {
                name,
                modifier: addTtModifier,
                description: "",
                includeGPS: addTtGps,
                alternativeNames: [name],
                sportReferenceList: Array.from(addTtSportRefs),
            };

            const newDocRef = doc(collection(db, "trainingTypes"));
            await setDoc(newDocRef, newTrainingType);
            await syncSportsTrainingTypeLinks(newDocRef.id, addTtSportRefs);

            setAddTtOpen(false);
            await fetchTrainingTypes();
            await fetchSports();
        } catch (error: any) {
            console.error("Error adding training type:", error);
            setAddTtError(`Failed to add training type: ${error.message}`);
        } finally {
            setAddingTrainingType(false);
        }
    };

    // Edit a training type's name, modifier, GPS flag, and sport references. If the name
    // changes, the previous name is preserved in alternativeNames so legacy mappings still resolve.
    const handleSaveTrainingType = async () => {
        if (savingTrainingType) return;
        if (!editingTrainingType?.id) return;
        if (isSpecialTrainingType(editingTrainingType.id)) return;
        if (!editTtName.trim()) return;

        try {
            setSavingTrainingType(true);
            setTrainingTypesError(null);

            const newName = editTtName.trim();
            const alternativeNames = dedupeStrings([
                newName,
                editingTrainingType.name,
                ...(editingTrainingType.alternativeNames ?? []),
            ]);

            const updated: TrainingType = {
                name: newName,
                modifier: editTtModifier,
                description: editingTrainingType.description,
                includeGPS: editTtGps,
                alternativeNames,
                sportReferenceList: Array.from(editTtSportRefs),
            };

            await setDoc(doc(db, "trainingTypes", editingTrainingType.id), updated);
            await syncSportsTrainingTypeLinks(editingTrainingType.id, editTtSportRefs);

            setEditingTrainingType(null);
            await fetchTrainingTypes();
            await fetchSports();
        } catch (error: any) {
            console.error("Error saving training type:", error);
            setTrainingTypesError(`Failed to save training type: ${error.message}`);
        } finally {
            setSavingTrainingType(false);
        }
    };

    const tabButton = (key: TabKey, label: string) => (
        <button
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
        >
            {label}
        </button>
    );

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-6 p-6 h-[calc(100vh-64px)] overflow-hidden">
                <h1 className="text-2xl font-heading">Training Types &amp; Sports</h1>

                {/* Top tabs */}
                <div className="flex items-center gap-2 border-b border-gray-200">
                    {tabButton("sports", "Sports")}
                    {tabButton("trainingTypes", "Training Types")}
                </div>

                {activeTab === "sports" && (
                    <div className="flex flex-col gap-4 flex-1 min-h-0">
                        <div className="flex items-start justify-between gap-4">
                            <p className="text-sm text-gray-500">
                                Sports from the <code className="text-xs">sports</code> collection.
                            </p>
                            <button
                                onClick={handleGenerateSports}
                                disabled={generatingSports || sports.length > 0}
                                className="btn btn-primary btn-sm !h-10 gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ArrowPathIcon className={`w-4 h-4 ${generatingSports ? "animate-spin" : ""}`} />
                                {generatingSports ? "Generating..." : "Generate Sports"}
                            </button>
                        </div>

                        {sportsError && (
                            <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg">
                                <span>{sportsError}</span>
                            </div>
                        )}

                        <div className="flex-1 min-h-0 overflow-y-auto">
                            {sportsLoading ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    Loading sports...
                                </div>
                            ) : sports.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    No sports yet. Click "Generate Sports" to populate from the old system.
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border overflow-hidden">
                                    <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase">
                                        <div className="col-span-3">Sport</div>
                                        <div className="col-span-8">Training Types</div>
                                        <div className="col-span-1 text-right">Edit</div>
                                    </div>
                                    {sports.map((sport) => (
                                        <div
                                            key={sport.id}
                                            className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50 items-start transition-colors"
                                        >
                                            <div className="col-span-3">
                                                <span className="text-sm font-medium text-gray-900">{sport.name}</span>
                                                <span className="block text-[10px] text-gray-400 mt-0.5">
                                                    {sport.trainingTypeIDs?.length ?? 0} linked
                                                </span>
                                            </div>
                                            <div className="col-span-8">
                                                {sport.trainingTypeIDs && sport.trainingTypeIDs.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {sport.trainingTypeIDs.map((id) => {
                                                            const special = getSpecialTrainingType(id);
                                                            const SpecialIcon = special?.icon;
                                                            return (
                                                                <span
                                                                    key={id}
                                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs border rounded-full ${
                                                                        special
                                                                            ? "bg-green-50 text-green-800 border-green-200"
                                                                            : "bg-blue-50 text-blue-800 border-blue-100"
                                                                    }`}
                                                                >
                                                                    {SpecialIcon && <SpecialIcon className="w-3 h-3" />}
                                                                    {trainingTypeNameById.get(id) ?? id}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">None linked yet.</span>
                                                )}
                                            </div>
                                            <div className="col-span-1 flex justify-end">
                                                <button
                                                    onClick={() => handleOpenEditSport(sport)}
                                                    className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                                    title="Edit sport"
                                                >
                                                    <PencilIcon className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "trainingTypes" && (
                    <div className="flex flex-col gap-4 flex-1 min-h-0">
                        <div className="flex items-start justify-between gap-4">
                            <p className="text-sm text-gray-500">
                                Training types from the <code className="text-xs">trainingTypes</code> collection.
                                {selectedIds.size > 0 && (
                                    <span className="ml-2 text-gray-700 font-medium">{selectedIds.size} selected</span>
                                )}
                                <span className="block mt-1 text-xs text-green-700">
                                    Rows in green are core training types used by the apps. They are locked and cannot
                                    be edited, renamed or combined.
                                </span>
                            </p>
                            <div className="flex items-center gap-2">
                                {selectedIds.size === 1 && (
                                    <button
                                        onClick={handleOpenRename}
                                        className="btn btn-sm !h-10 gap-2"
                                    >
                                        <PencilIcon className="w-4 h-4" />
                                        Rename
                                    </button>
                                )}
                                <button
                                    onClick={handleOpenCombine}
                                    disabled
                                    className="btn btn-sm !h-10 gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ArrowsPointingInIcon className="w-4 h-4" />
                                    Combine
                                </button>
                                <button
                                    onClick={handleGenerateTrainingTypes}
                                    disabled={generatingTrainingTypes || trainingTypes.length > 0}
                                    className="btn btn-primary btn-sm !h-10 gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ArrowPathIcon className={`w-4 h-4 ${generatingTrainingTypes ? "animate-spin" : ""}`} />
                                    {generatingTrainingTypes ? "Generating..." : "Generate Training Types"}
                                </button>
                                <button
                                    onClick={handleOpenAddTrainingType}
                                    className="btn btn-primary btn-sm !h-10 gap-2"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    Add Training Type
                                </button>
                            </div>
                        </div>

                        {trainingTypesError && (
                            <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg">
                                <span>{trainingTypesError}</span>
                            </div>
                        )}

                        <div>
                            <input
                                type="text"
                                placeholder="Filter training types (e.g. walk)"
                                className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                value={trainingTypeFilter}
                                onChange={(e) => setTrainingTypeFilter(e.target.value)}
                            />
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto">
                            {trainingTypesLoading ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    Loading training types...
                                </div>
                            ) : trainingTypes.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    No training types yet. Click "Generate Training Types" to populate from the old system.
                                </div>
                            ) : filteredTrainingTypes.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    No training types match "{trainingTypeFilter}".
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border overflow-hidden">
                                    <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase">
                                        <div className="col-span-1"></div>
                                        <div className="col-span-3">Name</div>
                                        <div className="col-span-2">ID</div>
                                        <div className="col-span-1">Mod</div>
                                        <div className="col-span-3">Linked Sports</div>
                                        <div className="col-span-1">GPS</div>
                                        <div className="col-span-1 text-right">Edit</div>
                                    </div>
                                    {filteredTrainingTypes.map((trainingType) => {
                                        const special = getSpecialTrainingType(trainingType.id);
                                        const SpecialIcon = special?.icon;
                                        return (
                                        <label
                                            key={trainingType.id}
                                            className={`grid grid-cols-12 gap-4 px-4 py-3 border-b items-center transition-colors ${
                                                special
                                                    ? "bg-green-50 hover:bg-green-100 cursor-default"
                                                    : "hover:bg-gray-50 cursor-pointer"
                                            }`}
                                        >
                                            <div className="col-span-1">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm disabled:opacity-30"
                                                    disabled={!!special}
                                                    title={special ? "Protected training type" : undefined}
                                                    checked={!!trainingType.id && selectedIds.has(trainingType.id)}
                                                    onChange={() => trainingType.id && toggleSelected(trainingType.id)}
                                                />
                                            </div>
                                            <div className="col-span-3 flex items-center gap-2 min-w-0">
                                                {SpecialIcon && (
                                                    <SpecialIcon
                                                        className="w-4 h-4 shrink-0 text-green-700"
                                                        title={`${special!.name} (protected)`}
                                                    />
                                                )}
                                                <span
                                                    className={`text-sm font-medium truncate ${
                                                        special ? "text-green-800" : "text-gray-900"
                                                    }`}
                                                >
                                                    {trainingType.name}
                                                </span>
                                            </div>
                                            <div className="col-span-2 min-w-0">
                                                <span
                                                    className={`block text-xs font-mono truncate ${
                                                        special ? "text-green-700" : "text-gray-500"
                                                    }`}
                                                    title={trainingType.id}
                                                >
                                                    {trainingType.id ?? "—"}
                                                </span>
                                            </div>
                                            <div className="col-span-1">
                                                <span
                                                    className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                                                        special
                                                            ? "bg-green-100 text-green-800"
                                                            : "bg-primary/10 text-primary"
                                                    }`}
                                                >
                                                    {trainingType.modifier}x
                                                </span>
                                            </div>
                                            <div className="col-span-3">
                                                {trainingType.id && (sportNamesByTrainingTypeId.get(trainingType.id)?.length ?? 0) > 0 ? (
                                                    (() => {
                                                        const allNames = sportNamesByTrainingTypeId.get(trainingType.id!)!;
                                                        const isExpanded = expandedSportLists.has(trainingType.id!);
                                                        const visibleNames = isExpanded ? allNames : allNames.slice(0, 10);
                                                        const remaining = allNames.length - visibleNames.length;
                                                        return (
                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                {visibleNames.map((sportName) => (
                                                                    <span
                                                                        key={sportName}
                                                                        className="inline-block px-2 py-0.5 text-xs bg-blue-50 text-blue-800 border border-blue-100 rounded-full"
                                                                    >
                                                                        {sportName}
                                                                    </span>
                                                                ))}
                                                                {remaining > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            toggleExpandedSportList(trainingType.id!);
                                                                        }}
                                                                        className="text-xs text-primary hover:underline font-medium"
                                                                    >
                                                                        +{remaining} more
                                                                    </button>
                                                                )}
                                                                {isExpanded && allNames.length > 10 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            toggleExpandedSportList(trainingType.id!);
                                                                        }}
                                                                        className="text-xs text-gray-500 hover:underline font-medium"
                                                                    >
                                                                        Show less
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })()
                                                ) : (
                                                    <span className="text-xs text-gray-400">None</span>
                                                )}
                                            </div>
                                            <div className="col-span-1">
                                                <span
                                                    className={`inline-block px-2 py-0.5 text-[10px] rounded-full ${
                                                        trainingType.includeGPS
                                                            ? "bg-green-100 text-green-800"
                                                            : "bg-gray-100 text-gray-500"
                                                    }`}
                                                >
                                                    {trainingType.includeGPS ? "GPS" : "No GPS"}
                                                </span>
                                            </div>
                                            <div className="col-span-1 flex justify-end">
                                                {special ? (
                                                    <span
                                                        className="p-1.5 rounded bg-green-100 text-green-700"
                                                        title="Protected training type — cannot be edited"
                                                    >
                                                        <LockClosedIcon className="w-3.5 h-3.5" />
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleOpenEditTrainingType(trainingType);
                                                        }}
                                                        className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                                        title="Edit training type"
                                                    >
                                                        <PencilIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Edit training type dialog */}
                {editingTrainingType && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-lg p-6 flex flex-col gap-4 max-h-[90vh]">
                            <h3 className="text-lg font-medium">Edit Training Type</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={editTtName}
                                    onChange={(e) => setEditTtName(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Load Modifier</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={editTtModifier}
                                    onChange={(e) => setEditTtModifier(parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm"
                                    checked={editTtGps}
                                    onChange={(e) => setEditTtGps(e.target.checked)}
                                />
                                <span className="text-sm text-gray-800">Include GPS</span>
                            </label>

                            <div className="flex flex-col min-h-0">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Sport References ({editTtSportRefs.size} selected)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleAssignEditTtToAllSports}
                                        className="text-xs px-2 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                    >
                                        Assign to All Sports
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Filter sports..."
                                    className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={editTtSportFilter}
                                    onChange={(e) => setEditTtSportFilter(e.target.value)}
                                />
                                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                                    {sports
                                        .filter((s) => s.name.toLowerCase().includes(editTtSportFilter.trim().toLowerCase()))
                                        .map((s) => (
                                            <label
                                                key={s.id}
                                                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={editTtSportRefs.has(s.name)}
                                                    onChange={() => toggleEditTtSportRef(s.name)}
                                                />
                                                <span className="text-sm text-gray-800">{s.name}</span>
                                            </label>
                                        ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-2">
                                <button
                                    onClick={() => setEditingTrainingType(null)}
                                    disabled={savingTrainingType}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveTrainingType}
                                    disabled={savingTrainingType || !editTtName.trim()}
                                    className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors disabled:opacity-40"
                                >
                                    {savingTrainingType ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit sport dialog */}
                {editingSport && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-lg p-6 flex flex-col gap-4 max-h-[90vh]">
                            <div>
                                <h3 className="text-lg font-medium">Edit Sport</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Change the name and select the training types associated with this sport.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sport Name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={editSportName}
                                    onChange={(e) => setEditSportName(e.target.value)}
                                />
                            </div>

                            <div className="flex flex-col min-h-0 flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Training Types ({editSportTrainingTypeIds.size} selected)
                                </label>
                                <input
                                    type="text"
                                    placeholder="Filter training types..."
                                    className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={editSportFilter}
                                    onChange={(e) => setEditSportFilter(e.target.value)}
                                />
                                <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                                    {trainingTypes
                                        .filter((t) => {
                                            const q = editSportFilter.trim().toLowerCase();
                                            if (!q) return true;
                                            const haystack = [t.name, ...(t.alternativeNames ?? [])].join(" ").toLowerCase();
                                            return haystack.includes(q);
                                        })
                                        .map((t) => {
                                            const special = getSpecialTrainingType(t.id);
                                            const SpecialIcon = special?.icon;
                                            return (
                                                <label
                                                    key={t.id}
                                                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                                                        special ? "bg-green-50 hover:bg-green-100" : "hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm"
                                                        checked={!!t.id && editSportTrainingTypeIds.has(t.id)}
                                                        onChange={() => t.id && toggleEditSportTrainingType(t.id)}
                                                    />
                                                    {SpecialIcon && <SpecialIcon className="w-4 h-4 text-green-700" />}
                                                    <span
                                                        className={`text-sm ${
                                                            special ? "text-green-800 font-medium" : "text-gray-800"
                                                        }`}
                                                    >
                                                        {t.name}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-2">
                                <button
                                    onClick={() => setEditingSport(null)}
                                    disabled={savingSport}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveSport}
                                    disabled={savingSport || !editSportName.trim()}
                                    className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors disabled:opacity-40"
                                >
                                    {savingSport ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Rename dialog */}
                {renameOpen && selectedTrainingTypes.length === 1 && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-md p-6 flex flex-col gap-4">
                            <div>
                                <h3 className="text-lg font-medium">Rename Training Type</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    The previous name is kept as an alternative name.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={renameName}
                                    onChange={(e) => setRenameName(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="flex justify-end gap-3 mt-2">
                                <button
                                    onClick={() => setRenameOpen(false)}
                                    disabled={renaming}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmRename}
                                    disabled={renaming || !renameName.trim()}
                                    className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors disabled:opacity-40"
                                >
                                    {renaming ? "Renaming..." : "Rename"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add training type dialog */}
                {addTtOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-lg p-6 flex flex-col gap-4 max-h-[90vh]">
                            <h3 className="text-lg font-medium">Add Training Type</h3>

                            {addTtError && (
                                <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg">
                                    {addTtError}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={addTtName}
                                    onChange={(e) => setAddTtName(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Load Modifier</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={addTtModifier}
                                    onChange={(e) => setAddTtModifier(parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm"
                                    checked={addTtGps}
                                    onChange={(e) => setAddTtGps(e.target.checked)}
                                />
                                <span className="text-sm text-gray-800">Include GPS</span>
                            </label>

                            <div className="flex flex-col min-h-0">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Sport References ({addTtSportRefs.size} selected)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleAssignAddTtToAllSports}
                                        className="text-xs px-2 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                    >
                                        Assign to All Sports
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Filter sports..."
                                    className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={addTtSportFilter}
                                    onChange={(e) => setAddTtSportFilter(e.target.value)}
                                />
                                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                                    {sports
                                        .filter((s) => s.name.toLowerCase().includes(addTtSportFilter.trim().toLowerCase()))
                                        .map((s) => (
                                            <label
                                                key={s.id}
                                                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={addTtSportRefs.has(s.name)}
                                                    onChange={() => toggleAddTtSportRef(s.name)}
                                                />
                                                <span className="text-sm text-gray-800">{s.name}</span>
                                            </label>
                                        ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-2">
                                <button
                                    onClick={() => setAddTtOpen(false)}
                                    disabled={addingTrainingType}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmAddTrainingType}
                                    disabled={addingTrainingType || !addTtName.trim()}
                                    className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors disabled:opacity-40"
                                >
                                    {addingTrainingType ? "Adding..." : "Add"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Combine dialog */}
                {combineOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-lg p-6 flex flex-col gap-4">
                            <div>
                                <h3 className="text-lg font-medium">Combine Training Types</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Combining {selectedTrainingTypes.length} training types into one. The others will be
                                    deleted and their names preserved as alternative names.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Combined Name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={combineName}
                                    onChange={(e) => setCombineName(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Load Modifier</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={combineModifier}
                                    onChange={(e) => setCombineModifier(parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Alternative Names Captured ({mergedAlternativeNames.length})
                                </label>
                                <div className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
                                    {mergedAlternativeNames.map((name) => (
                                        <span
                                            key={name}
                                            className="inline-block px-2 py-0.5 text-xs bg-white border border-gray-200 rounded-full text-gray-700"
                                        >
                                            {name}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Sports Captured ({mergedSportReferenceList.length})
                                </label>
                                <div className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
                                    {mergedSportReferenceList.length === 0 ? (
                                        <span className="text-xs text-gray-400">None</span>
                                    ) : (
                                        mergedSportReferenceList.map((sport) => (
                                            <span
                                                key={sport}
                                                className="inline-block px-2 py-0.5 text-xs bg-white border border-gray-200 rounded-full text-gray-700"
                                            >
                                                {sport}
                                            </span>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-2">
                                <button
                                    onClick={() => setCombineOpen(false)}
                                    disabled={combining}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmCombine}
                                    disabled={combining || !combineName.trim()}
                                    className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors disabled:opacity-40"
                                >
                                    {combining ? "Combining..." : "Combine"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
};

export default TrainingTypesAndSportsPage;